import React, {useRef, useEffect, useImperativeHandle, forwardRef, useCallback} from 'react';
import {Box} from '@material-ui/core';

/**
 * 0.1Hz 단위 FFT 라인 차트
 * ch1(좌뇌), ch2(우뇌)를 2D 라인 그래프로 표시
 */
const FFTLineChart = forwardRef(({width = 900, height = 500, maxHz = 50}, ref) => {
    const canvasRef = useRef(null)
    const dataRef = useRef({ch1: [], ch2: []})
    const animRef = useRef(null)

    useImperativeHandle(ref, () => ({
        update: (data) => {
            dataRef.current = {
                ch1: data.ch1FFT || [],
                ch2: data.ch2FFT || []
            }
            draw()
        },
        clear: () => {
            dataRef.current = {ch1: [], ch2: []}
            draw()
        }
    }))

    const draw = useCallback(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        const w = canvas.width
        const h = canvas.height
        const {ch1, ch2} = dataRef.current

        ctx.clearRect(0, 0, w, h)

        // 배경
        ctx.fillStyle = '#1a1a2e'
        ctx.fillRect(0, 0, w, h)

        const binCount = Math.max(ch1.length, ch2.length, 1)
        const hzPerBin = maxHz / binCount
        const displayBins = Math.min(binCount, Math.floor(maxHz / hzPerBin))

        // 최대값 계산 (auto scale)
        let maxVal = 0
        for (let i = 0; i < displayBins; i++) {
            if (ch1[i] > maxVal) maxVal = ch1[i]
            if (ch2[i] > maxVal) maxVal = ch2[i]
        }
        if (maxVal === 0) maxVal = 1

        const padding = {top: 30, bottom: 40, left: 50, right: 20}
        const chartW = w - padding.left - padding.right
        const chartH = h - padding.top - padding.bottom

        // 그리드
        ctx.strokeStyle = 'rgba(255,255,255,0.1)'
        ctx.lineWidth = 1

        // 세로 그리드 (5Hz 간격)
        ctx.font = '11px sans-serif'
        ctx.fillStyle = 'rgba(255,255,255,0.5)'
        ctx.textAlign = 'center'
        for (let hz = 0; hz <= maxHz; hz += 5) {
            const x = padding.left + (hz / maxHz) * chartW
            ctx.beginPath()
            ctx.moveTo(x, padding.top)
            ctx.lineTo(x, h - padding.bottom)
            ctx.stroke()
            ctx.fillText(`${hz}Hz`, x, h - padding.bottom + 15)
        }

        // 1Hz 간격 얇은 그리드
        ctx.strokeStyle = 'rgba(255,255,255,0.04)'
        for (let hz = 0; hz <= maxHz; hz += 1) {
            const x = padding.left + (hz / maxHz) * chartW
            ctx.beginPath()
            ctx.moveTo(x, padding.top)
            ctx.lineTo(x, h - padding.bottom)
            ctx.stroke()
        }

        // 가로 그리드
        ctx.strokeStyle = 'rgba(255,255,255,0.1)'
        ctx.textAlign = 'right'
        const ySteps = 5
        for (let i = 0; i <= ySteps; i++) {
            const y = padding.top + (i / ySteps) * chartH
            const val = (maxVal * (ySteps - i) / ySteps).toFixed(1)
            ctx.beginPath()
            ctx.moveTo(padding.left, y)
            ctx.lineTo(w - padding.right, y)
            ctx.stroke()
            ctx.fillStyle = 'rgba(255,255,255,0.5)'
            ctx.fillText(val, padding.left - 5, y + 4)
        }

        // 주파수 대역 배경
        const bands = [
            {name: 'Delta', start: 0, end: 4, color: 'rgba(128,0,255,0.08)'},
            {name: 'Theta', start: 4, end: 8, color: 'rgba(0,128,255,0.08)'},
            {name: 'Alpha', start: 8, end: 13, color: 'rgba(0,255,128,0.08)'},
            {name: 'SMR', start: 13, end: 15, color: 'rgba(255,255,0,0.08)'},
            {name: 'Beta', start: 15, end: 30, color: 'rgba(255,128,0,0.08)'},
            {name: 'Gamma', start: 30, end: 50, color: 'rgba(255,0,0,0.08)'},
        ]
        ctx.font = '10px sans-serif'
        ctx.textAlign = 'center'
        bands.forEach(band => {
            if (band.start >= maxHz) return
            const x1 = padding.left + (band.start / maxHz) * chartW
            const x2 = padding.left + (Math.min(band.end, maxHz) / maxHz) * chartW
            ctx.fillStyle = band.color
            ctx.fillRect(x1, padding.top, x2 - x1, chartH)
            ctx.fillStyle = 'rgba(255,255,255,0.3)'
            ctx.fillText(band.name, (x1 + x2) / 2, padding.top + 12)
        })

        // 라인 그리기 함수
        const drawLine = (data, color) => {
            if (!data || data.length === 0) return
            ctx.strokeStyle = color
            ctx.lineWidth = 1.5
            ctx.beginPath()
            for (let i = 0; i < displayBins; i++) {
                const hz = i * hzPerBin
                const x = padding.left + (hz / maxHz) * chartW
                const val = Math.min(data[i] || 0, maxVal)
                const y = padding.top + chartH - (val / maxVal) * chartH
                if (i === 0) ctx.moveTo(x, y)
                else ctx.lineTo(x, y)
            }
            ctx.stroke()
        }

        // ch1 (좌뇌) - 파란색
        drawLine(ch1, '#4FC3F7')
        // ch2 (우뇌) - 빨간색
        drawLine(ch2, '#EF5350')

        // 범례
        ctx.font = '12px sans-serif'
        ctx.fillStyle = '#4FC3F7'
        ctx.textAlign = 'left'
        ctx.fillText('● CH1 (좌뇌)', padding.left + 10, padding.top - 10)
        ctx.fillStyle = '#EF5350'
        ctx.fillText('● CH2 (우뇌)', padding.left + 130, padding.top - 10)

        // bin 정보
        ctx.fillStyle = 'rgba(255,255,255,0.4)'
        ctx.font = '10px sans-serif'
        ctx.textAlign = 'right'
        ctx.fillText(`${binCount}bins / ${hzPerBin.toFixed(2)}Hz per bin`, w - padding.right, padding.top - 10)

    }, [maxHz, width, height])

    useEffect(() => {
        draw()
    }, [draw])

    return (
        <Box>
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{display: 'block', borderRadius: 4}}
            />
        </Box>
    )
})

export default FFTLineChart
